import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  InputField,
  Modal,
  Select,
  ToggleSwitch,
} from '@enamelai/design-system'
import {
  IWatchFolderMount,
  IWatchFolderMountPayload,
  PatientRouting,
} from '@dataTypes/dicom-capture.type'
import { IOperatory } from '@dataTypes/appointment.type'
import {
  buildOperatoryOptions,
  fromOperatoryOption,
  PATIENT_ROUTING_OPTIONS,
  toOperatoryOption,
} from './dicomCapture.data'

interface WatchFolderFormData {
  label: string
  root_path: string
  recursive: boolean
  delete_on_success: boolean
  operatory_id: string
  default_patient_routing: PatientRouting
  enabled: boolean
}

type ToggleField = 'recursive' | 'delete_on_success' | 'enabled'

const TOGGLES: { field: ToggleField; label: string }[] = [
  { field: 'recursive', label: 'Recursive' },
  { field: 'delete_on_success', label: 'Delete on success' },
  { field: 'enabled', label: 'Enabled' },
]

const emptyForm = (): WatchFolderFormData => ({
  label: '',
  root_path: '',
  recursive: true,
  delete_on_success: false,
  operatory_id: '',
  default_patient_routing: 'from_dicom_tag',
  enabled: true,
})

const mountToForm = (mount: IWatchFolderMount): WatchFolderFormData => ({
  label: mount.label,
  root_path: mount.root_path,
  recursive: mount.recursive,
  delete_on_success: mount.delete_on_success,
  operatory_id: mount.operatory_id ?? '',
  default_patient_routing: mount.default_patient_routing,
  enabled: mount.enabled,
})

type AddWatchFolderModalProps = {
  isOpen: boolean
  clinicId: string
  operatories: IOperatory[]
  mode?: 'add' | 'edit'
  mount?: IWatchFolderMount | null
  onRequestClose: () => void
  onSubmit: (
    payload: IWatchFolderMountPayload,
    editId?: string,
  ) => Promise<void>
  isSubmitting?: boolean
}

const AddWatchFolderModal: React.FC<AddWatchFolderModalProps> = ({
  isOpen,
  clinicId,
  operatories,
  mode = 'add',
  mount = null,
  onRequestClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<WatchFolderFormData>(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const wasOpenRef = useRef(false)

  const operatoryOptions = useMemo(
    () => buildOperatoryOptions(operatories),
    [operatories],
  )

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return
    setFormData(mount ? mountToForm(mount) : emptyForm())
    setErrors({})
  }, [isOpen, mount])

  const handleChange = useCallback(
    <K extends keyof WatchFolderFormData>(
      field: K,
      value: WatchFolderFormData[K],
    ): void => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      setErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field as string]
        return next
      })
    },
    [],
  )

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {}
    if (!formData.label.trim()) next.label = 'Label is required'
    if (!formData.root_path.trim()) next.root_path = 'Folder path is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }, [formData])

  const handleClose = useCallback((): void => {
    setFormData(emptyForm())
    setErrors({})
    onRequestClose()
  }, [onRequestClose])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!validate()) return
    const payload: IWatchFolderMountPayload = {
      clinic_id: clinicId,
      operatory_id: formData.operatory_id || null,
      label: formData.label.trim(),
      root_path: formData.root_path.trim(),
      recursive: formData.recursive,
      delete_on_success: formData.delete_on_success,
      default_patient_routing: formData.default_patient_routing,
      enabled: formData.enabled,
    }
    try {
      await onSubmit(payload, mount?.watch_folder_mount_id)
      handleClose()
    } catch {
      // Parent surfaces the error toast; keep the modal open for corrections.
    }
  }, [clinicId, formData, handleClose, mount, onSubmit, validate])

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={mode === 'edit' ? 'Edit watch folder' : 'Add watch folder'}
      closeOnBackdrop={false}
      className="dc-modal"
      secondaryLabel="Cancel"
      onSecondaryClick={handleClose}
      primaryLabel={mode === 'edit' ? 'Save' : 'Add'}
      primaryDisabled={isSubmitting}
      onPrimaryClick={() => {
        void handleSave()
      }}
    >
      <form
        className="dc-modal__form"
        onSubmit={(e): void => {
          e.preventDefault()
          void handleSave()
        }}
      >
        <InputField
          variant="text"
          label="Label"
          placeholder="e.g. Scanner drop folder"
          value={formData.label}
          onChange={(e) => handleChange('label', e.target.value)}
          error={!!errors.label}
          helperText={errors.label}
          fullWidth
        />

        <InputField
          variant="text"
          label="Folder path"
          placeholder="e.g. /mnt/dicom-in or \\\\server\\share"
          value={formData.root_path}
          onChange={(e) => handleChange('root_path', e.target.value)}
          error={!!errors.root_path}
          helperText={
            errors.root_path ||
            'Path on the ingest host. Validated server-side that it exists & is readable.'
          }
          fullWidth
        />

        <div className="dc-modal__row">
          <Select
            label="Operatory / chair (optional)"
            options={operatoryOptions}
            value={toOperatoryOption(formData.operatory_id)}
            onChange={(value) =>
              handleChange('operatory_id', fromOperatoryOption(String(value)))
            }
            helperText="When assigned, images route to the patient currently open at this chair."
          />
          <Select
            label="Patient routing (fallback)"
            options={PATIENT_ROUTING_OPTIONS}
            value={formData.default_patient_routing}
            onChange={(value) =>
              handleChange('default_patient_routing', value as PatientRouting)
            }
          />
        </div>

        <div className="dc-modal__toggles">
          {TOGGLES.map(({ field, label }) => (
            <label
              key={field}
              className="dc-modal__toggle"
              htmlFor={`dicom-watch-${field}`}
            >
              <ToggleSwitch
                id={`dicom-watch-${field}`}
                checked={formData[field]}
                onChange={(checked) => handleChange(field, checked)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </form>
    </Modal>
  )
}

export default AddWatchFolderModal
